import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiChevronRight } from 'react-icons/fi';
import { FaGithub, FaLinkedin, FaTwitter } from 'react-icons/fa';
import LensToggle from './LensToggle';
import './Navbar.css';

const navItems = [
  { href: '#hero', label: 'Home' },
  { href: '#about', label: 'About' },
  { href: '#experience', label: 'Experience' },
  { href: '#honours', label: 'Honours & Awards' },
  { href: '#projects', label: 'Projects' },
  { href: '#skills', label: 'Skills' },
  { href: '#oss', label: 'Open Source' },
  { href: '#hobby', label: 'Hobby' },
  { href: '#playground', label: 'Playground' },
  { href: '#contact', label: 'Contact' },
];

const CONTACT_EMAIL = 'tanmayrajurkar1915@gmail.com';

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('#hero');

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    let rafId = null;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const offsets = navItems.map((item) => {
          const el = document.querySelector(item.href);
          if (!el) return { href: item.href, top: Infinity };
          const rect = el.getBoundingClientRect();
          return { href: item.href, top: Math.abs(rect.top) };
        });
        const closest = offsets.reduce(
          (a, b) => (a.top < b.top ? a : b),
          { href: '', top: Infinity }
        );
        setActiveSection(closest.href);
        rafId = null;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const year = new Date().getFullYear();

  return (
    <nav
      className={`navbar${menuOpen ? ' navbar--menu-open' : ''}`}
      aria-label="Main navigation"
    >
      <a href="#hero" className="nav-logo" onClick={closeMenu}>
        T.R<span>.</span>
      </a>
      <div className="nav-lens">
        <LensToggle size="sm" />
      </div>
      <button
        type="button"
        className={`hamburger ${menuOpen ? 'open' : ''}`}
        aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={menuOpen}
        aria-controls="nav-menu-panel"
        onClick={() => setMenuOpen((open) => !open)}
      >
        <span className="bar" />
        <span className="bar" />
        <span className="bar" />
      </button>

      <div
        className={`nav-overlay ${menuOpen ? 'nav-overlay--open' : ''}`}
        aria-hidden={!menuOpen}
      >
        <button
          type="button"
          className="nav-overlay-dismiss"
          aria-label="Close menu"
          tabIndex={menuOpen ? 0 : -1}
          onClick={closeMenu}
        />
        <div className="nav-panel-anchor">
          <button
            type="button"
            className="nav-close"
            aria-label="Close menu"
            tabIndex={menuOpen ? 0 : -1}
            onClick={closeMenu}
          >
            <FiX aria-hidden />
          </button>
          <div
            id="nav-menu-panel"
            className="nav-menu-card"
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
          >
            <span className="nav-menu-heading">Index · contents</span>
            <ul className="nav-menu-list">
              {navItems.map((item, index) => {
                const num = String(index + 1).padStart(2, '0');
                const isActive = activeSection === item.href;
                return (
                  <li key={item.href}>
                    <a
                      href={item.href}
                      className={`nav-menu-row ${isActive ? 'active' : ''}`}
                      onClick={closeMenu}
                    >
                      <span className="nav-item-index">{num}</span>
                      <span className="nav-item-label">{item.label}</span>
                      <span className="nav-item-chevron" aria-hidden>
                        <FiChevronRight />
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>

            <div className="nav-menu-footer">
              <div className="nav-menu-divider" />
              <div className="nav-menu-socials">
                <a
                  href="https://github.com/tanmayrajurkar"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="GitHub"
                  tabIndex={menuOpen ? 0 : -1}
                >
                  <FaGithub />
                </a>
                <a
                  href="https://www.linkedin.com/in/tanmay-rajurkar-254305227/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                  tabIndex={menuOpen ? 0 : -1}
                >
                  <FaLinkedin />
                </a>
                <a
                  href="https://x.com/tanmay_rajurkar"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Twitter"
                  tabIndex={menuOpen ? 0 : -1}
                >
                  <FaTwitter />
                </a>
              </div>
              <a
                className="nav-menu-email"
                href={`mailto:${CONTACT_EMAIL}`}
                tabIndex={menuOpen ? 0 : -1}
              >
                {CONTACT_EMAIL}
              </a>
              <p className="nav-menu-copy">
                © {year} Tanmay Rajurkar. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
